import { QALITY_TEST_ISSUE_TYPE } from '@qality/common-config/src/constants';
import {
  Folder,
  ProjectSelectOption,
  RepositoryIssue,
  Status,
  TestCaseInFolder,
  TestType,
} from '@qality/common-model';
import { IssueTestType } from '@qality/common-model/src/TestType';
import { StorageOfUniqueServices } from '@qality/common-service';
import { StringUtils } from '@qality/common-utils';

export const filterOutIssues = (
  allIssuesInFolder: RepositoryIssue[],
  filters: {
    projectsForQuery: ProjectSelectOption[];
    statusesForQuery: Status[];
    textSearchFilter: string;
    testTypesForQuery: TestType[];
  },
  testCasesTestTypes: Array<IssueTestType>
): RepositoryIssue[] => {
  const {
    projectsForQuery,
    statusesForQuery,
    testTypesForQuery,
    textSearchFilter,
  } = filters;
  // server id -> number, cloud -> string, converting everything to string to avoid invalid types
  const projectIds = projectsForQuery
    .map((projectFilterOption) => projectFilterOption.id.toString())
    .filter((projectId) => projectId);

  const statusesNames = statusesForQuery
    .map((statusFilter) => statusFilter.name)
    .filter((statusName) => statusName);

  let newIssues = allIssuesInFolder
    .filter((issue) => issue.key) // filter out issues without permission -> no key -> no issue fetched
    .filter(
      (issue) =>
        projectIds.length === 0 ||
        projectIds.includes(issue.fields.project.id.toString())
    ) // filter out by project multi select -> any value needs to match
    .filter(
      (issue) =>
        statusesNames.length === 0 ||
        statusesNames.includes(issue.fields.status.name)
    ) // filter out by status multi select -> any value needs to mach name, NOT id
    .filter((issue) => {
      const sanitizedValue =
        StringUtils.removeWhitespace(textSearchFilter).toLowerCase();

      const sanitizedKey = StringUtils.removeWhitespace(
        issue.key
      ).toLowerCase();

      const sanitizedSummary = StringUtils.removeWhitespace(
        issue.fields.summary
      ).toLowerCase();

      return (
        sanitizedKey.includes(sanitizedValue) ||
        sanitizedSummary.includes(sanitizedValue)
      );
    }); // filter out by typed text -> key or summary needs to match value.

  if (StorageOfUniqueServices.get('LicenseService').isAdvanced) {
    newIssues = filterOutIssuesByTestTypes(
      newIssues,
      testTypesForQuery,
      testCasesTestTypes
    );
  }

  return newIssues;
};

export const getFolderIssues = async (
  currentFolder: Folder
): Promise<RepositoryIssue[]> => {
  return getSelectedFolderIssues(currentFolder.testCases);
};

export const getSelectedFolderIssues = async (
  testCasesInFolder: Array<TestCaseInFolder>
): Promise<RepositoryIssue[]> => {
  const issueIds = testCasesInFolder.map(
    (testCaseInFolder) => testCaseInFolder.testCaseId
  );

  const jiraIssues = await StorageOfUniqueServices.get(
    'JQLSearchService'
  ).bulkFetchIssuesByIds({
    ids: issueIds,
    maxResults: 100,
    fields: [
      'summary',
      'issuetype',
      'project',
      'status',
      'assignee',
      'priority',
    ],
  });

  // Filter out non-QAlity Test issues
  const notQAlityIssues = jiraIssues.filter(
    (issue) => issue.fields.issuetype.name !== QALITY_TEST_ISSUE_TYPE
  );

  // Remove test cases corresponding to non-QAlity issues
  const filteredTestCases = testCasesInFolder.filter(
    (testCaseInFolder) =>
      !notQAlityIssues.some(
        (issue) => issue.id === testCaseInFolder.testCaseId.toString()
      )
  );

  return filteredTestCases
    .map((testCaseInFolder) => {
      const jiraIssue = jiraIssues.find(
        (issue) => issue.id === testCaseInFolder.testCaseId.toString()
      );

      return {
        ...(jiraIssue || {}),
        testCaseInFolder,
      } as RepositoryIssue;
    })
    .sort((a, b) => a.testCaseInFolder.position - b.testCaseInFolder.position);
};

export const getAreAllNewIssuesInFolderMatchingFilterCriteria = (
  newIssuesInFolder: Array<RepositoryIssue>,
  newFilteredIssues: Array<RepositoryIssue>
): boolean => {
  return newIssuesInFolder.every((newIssueInFolder) => {
    return newFilteredIssues.find((filteredIssue) => {
      return (
        filteredIssue.testCaseInFolder.id ===
        newIssueInFolder.testCaseInFolder.id
      );
    });
  });
};

export const getCreateTestCaseInAllTestCasesWithFiltersFlagMessage = (
  issuesCount: number
): string => {
  return issuesCount === 1
    ? `1 QAlity test case has been created, but it doesn't match the current filter criteria.`
    : [
        `${issuesCount} QAlity test cases have been created, `,
        `but some of the test cases don't meet the current filter criteria.`,
      ].join('');
};

export const getCreateTestCaseInFolderFlagMessage = (
  issuesCount: number
): string => {
  return issuesCount === 1
    ? `1 QAlity test case has been added to this folder.`
    : `${issuesCount} QAlity test cases have been added to this folder.`;
};

export const getCreateTestCaseInFolderWithFiltersFlagMessage = (
  issuesCount: number
): string => {
  return issuesCount === 1
    ? `1 QAlity test case has been added to this folder, but it doesn't match the current filter criteria.`
    : [
        `${issuesCount} QAlity test cases have been added to this folder, `,
        `but some of the test cases don't meet the current filter criteria.`,
      ].join('');
};

const filterOutIssuesByTestTypes = (
  issuesToFilterByTestType: RepositoryIssue[],
  testTypesForQuery: TestType[],
  testCasesTestTypes: Array<IssueTestType>
): RepositoryIssue[] => {
  if (!testTypesForQuery.length) {
    return issuesToFilterByTestType;
  }

  const testCasesThatMatchFilter = testCasesTestTypes.filter((issueTestType) =>
    testTypesForQuery.some(
      (testTypeForQuery) => issueTestType.testTypeId === testTypeForQuery.id
    )
  );

  return issuesToFilterByTestType.filter((issueToFilterByTestType) =>
    testCasesThatMatchFilter.some(
      (testCaseThatMatchFilter) =>
        testCaseThatMatchFilter.testCaseId === +issueToFilterByTestType.id
    )
  );
};
